import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Plus, ArrowUp, ArrowDown, Trash2, Archive, ArchiveRestore, CheckSquare } from 'lucide-react';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { tareasKeys, useTablero, useTableros } from '@/hooks/useTareas';
import type { Tablero, TableroColumna } from '@/types/tareas';

const campo = 'px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300';
const COLOR_DEFAULT = '#94a3b8';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableroActualId?: string;
  onTableroCreado?: (id: string) => void;
}

export function AdministrarTablerosDialog({ open, onOpenChange, tableroActualId, onTableroCreado }: Props) {
  const qc = useQueryClient();
  const { data: tablerosData } = useTableros(open);
  const [seleccion, setSeleccion] = useState<string | undefined>(tableroActualId);
  const [nuevoTablero, setNuevoTablero] = useState('');
  const [nuevaColumna, setNuevaColumna] = useState('');

  useEffect(() => { if (open) setSeleccion(tableroActualId); }, [open, tableroActualId]);

  const { data: tablero } = useTablero(open ? seleccion : undefined);
  const columnas = tablero?.columnas ?? [];

  const refrescar = () => {
    qc.invalidateQueries({ queryKey: tareasKeys.tableros });
    if (seleccion) qc.invalidateQueries({ queryKey: tareasKeys.tablero(seleccion) });
  };
  const alFallar = (e: unknown) => toast.error(getErrorMessage(e, 'No se pudo guardar el cambio'));

  const crearTablero = useMutation({
    mutationFn: (nombre: string) => api.post<Tablero>('/api/tareas/tableros', { nombre }).then(r => r.data),
    onSuccess: (t) => { setNuevoTablero(''); setSeleccion(t.id); onTableroCreado?.(t.id); refrescar(); },
    onError: alFallar,
  });
  const actualizarTablero = useMutation({
    mutationFn: ({ id, ...body }: { id: string; nombre?: string; archivado?: boolean }) =>
      api.patch(`/api/tareas/tableros/${id}`, body).then(r => r.data),
    onSuccess: refrescar,
    onError: alFallar,
  });
  const crearColumna = useMutation({
    mutationFn: (nombre: string) => api.post(`/api/tareas/tableros/${seleccion}/columnas`, { nombre }).then(r => r.data),
    onSuccess: () => { setNuevaColumna(''); refrescar(); },
    onError: alFallar,
  });
  const actualizarColumna = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<Pick<TableroColumna, 'nombre' | 'color' | 'esFinal' | 'limiteWip'>>) =>
      api.patch(`/api/tareas/columnas/${id}`, body).then(r => r.data),
    onSuccess: refrescar,
    onError: alFallar,
  });
  const ordenarColumnas = useMutation({
    mutationFn: (ids: string[]) => api.patch(`/api/tareas/tableros/${seleccion}/columnas/orden`, { ids }).then(r => r.data),
    onSuccess: refrescar,
    onError: alFallar,
  });
  const eliminarColumna = useMutation({
    mutationFn: ({ id, moverA }: { id: string; moverA?: string }) =>
      api.delete(`/api/tareas/columnas/${id}`, { params: { moverA } }).then(r => r.data),
    onSuccess: refrescar,
    onError: alFallar,
  });

  const mover = (indice: number, delta: -1 | 1) => {
    const ids = columnas.map(c => c.id);
    const destino = indice + delta;
    if (destino < 0 || destino >= ids.length) return;
    [ids[indice], ids[destino]] = [ids[destino], ids[indice]];
    ordenarColumnas.mutate(ids);
  };

  const tableros = tablerosData?.tableros ?? [];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-hidden flex flex-col focus:outline-none">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <Dialog.Title className="text-lg font-semibold text-text-primary">Administrar tableros</Dialog.Title>
            <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100" aria-label="Cerrar"><X size={18} /></Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">Crea tableros y edita sus columnas</Dialog.Description>

          <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-[14rem_1fr]">
            <aside className="border-b md:border-b-0 md:border-r border-gray-100 p-3 space-y-1">
              {tableros.map(t => (
                <button
                  key={t.id}
                  onClick={() => setSeleccion(t.id)}
                  className={cn('w-full text-left px-3 py-2 rounded-lg text-sm truncate', seleccion === t.id ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50 text-text-primary', t.archivado && 'opacity-60')}
                >
                  {t.nombre}{t.archivado && ' (archivado)'}
                </button>
              ))}
              <form
                onSubmit={e => { e.preventDefault(); if (nuevoTablero.trim()) crearTablero.mutate(nuevoTablero.trim()); }}
                className="flex gap-1 pt-2"
              >
                <input value={nuevoTablero} onChange={e => setNuevoTablero(e.target.value)} placeholder="Nuevo tablero" maxLength={150} className={cn(campo, 'flex-1 min-w-0')} />
                <button type="submit" disabled={!nuevoTablero.trim() || crearTablero.isPending} className="p-2 rounded-lg bg-primary-500 text-white disabled:opacity-50" aria-label="Crear tablero">
                  <Plus size={14} />
                </button>
              </form>
            </aside>

            <section className="p-4 space-y-4">
              {!tablero && <p className="text-sm text-text-secondary">Selecciona un tablero.</p>}
              {tablero && (
                <>
                  <div className="flex items-center gap-2">
                    <input
                      key={tablero.tablero.id}
                      defaultValue={tablero.tablero.nombre}
                      onBlur={e => {
                        const nombre = e.target.value.trim();
                        if (nombre && nombre !== tablero.tablero.nombre) actualizarTablero.mutate({ id: tablero.tablero.id, nombre });
                        else e.target.value = tablero.tablero.nombre;
                      }}
                      maxLength={150}
                      className={cn(campo, 'flex-1 font-medium')}
                      aria-label="Nombre del tablero"
                    />
                    <button
                      onClick={() => actualizarTablero.mutate({ id: tablero.tablero.id, archivado: !tablero.tablero.archivado })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-text-secondary hover:bg-gray-50"
                    >
                      {tablero.tablero.archivado ? <><ArchiveRestore size={14} /> Restaurar</> : <><Archive size={14} /> Archivar</>}
                    </button>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">Columnas</h4>
                    <ul className="space-y-2">
                      {columnas.map((c, i) => (
                        <FilaColumna
                          key={c.id}
                          columna={c}
                          cantidadTareas={tablero.tareas.filter(t => t.columnaId === c.id).length}
                          otras={columnas.filter(o => o.id !== c.id)}
                          esPrimera={i === 0}
                          esUltima={i === columnas.length - 1}
                          unica={columnas.length === 1}
                          onCambio={cambios => actualizarColumna.mutate({ id: c.id, ...cambios })}
                          onMover={delta => mover(i, delta)}
                          onEliminar={moverA => eliminarColumna.mutate({ id: c.id, moverA })}
                        />
                      ))}
                    </ul>
                    <form
                      onSubmit={e => { e.preventDefault(); if (nuevaColumna.trim()) crearColumna.mutate(nuevaColumna.trim()); }}
                      className="flex gap-2 mt-3"
                    >
                      <input value={nuevaColumna} onChange={e => setNuevaColumna(e.target.value)} placeholder="Nueva columna" maxLength={100} className={cn(campo, 'flex-1')} />
                      <button type="submit" disabled={!nuevaColumna.trim() || crearColumna.isPending} className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-sm disabled:opacity-50">
                        Agregar
                      </button>
                    </form>
                    <p className="mt-2 text-[11px] text-text-secondary">
                      Las tareas que llegan a una columna marcada como final se consideran completadas.
                    </p>
                  </div>
                </>
              )}
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface FilaProps {
  columna: TableroColumna;
  cantidadTareas: number;
  otras: TableroColumna[];
  esPrimera: boolean;
  esUltima: boolean;
  unica: boolean;
  onCambio: (cambios: Partial<Pick<TableroColumna, 'nombre' | 'color' | 'esFinal' | 'limiteWip'>>) => void;
  onMover: (delta: -1 | 1) => void;
  onEliminar: (moverA?: string) => void;
}

function FilaColumna({ columna, cantidadTareas, otras, esPrimera, esUltima, unica, onCambio, onMover, onEliminar }: FilaProps) {
  const [eliminando, setEliminando] = useState(false);
  const [destino, setDestino] = useState('');

  return (
    <li className="rounded-lg border border-gray-200 p-2 space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="color"
          defaultValue={columna.color ?? COLOR_DEFAULT}
          onBlur={e => { if (e.target.value !== (columna.color ?? COLOR_DEFAULT)) onCambio({ color: e.target.value }); }}
          className="w-8 h-8 rounded cursor-pointer border border-gray-200 p-0.5 flex-shrink-0"
          aria-label="Color"
        />
        <input
          defaultValue={columna.nombre}
          onBlur={e => {
            const nombre = e.target.value.trim();
            if (nombre && nombre !== columna.nombre) onCambio({ nombre });
            else e.target.value = columna.nombre;
          }}
          maxLength={100}
          className={cn(campo, 'flex-1 min-w-0')}
          aria-label="Nombre de la columna"
        />
        <label className="inline-flex items-center gap-1 text-xs text-text-secondary whitespace-nowrap cursor-pointer" title="Las tareas en esta columna se consideran completadas">
          <input type="checkbox" checked={columna.esFinal} onChange={e => onCambio({ esFinal: e.target.checked })} />
          <CheckSquare size={12} /> Final
        </label>
        <button disabled={esPrimera} onClick={() => onMover(-1)} className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30" aria-label="Subir"><ArrowUp size={14} /></button>
        <button disabled={esUltima} onClick={() => onMover(1)} className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30" aria-label="Bajar"><ArrowDown size={14} /></button>
        <button disabled={unica} onClick={() => (cantidadTareas > 0 ? setEliminando(true) : onEliminar())} className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30" aria-label="Eliminar columna">
          <Trash2 size={14} />
        </button>
      </div>
      {eliminando && (
        <div className="flex flex-wrap items-center gap-2 bg-red-50 rounded-lg px-3 py-2 text-sm">
          <span className="text-red-700">Esta columna tiene {cantidadTareas} tarea{cantidadTareas === 1 ? '' : 's'}. Moverlas a:</span>
          <select value={destino} onChange={e => setDestino(e.target.value)} className={campo}>
            <option value="">Elegir columna…</option>
            {otras.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
          </select>
          <button disabled={!destino} onClick={() => { onEliminar(destino); setEliminando(false); }} className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm disabled:opacity-50">
            Mover y eliminar
          </button>
          <button onClick={() => setEliminando(false)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm">Cancelar</button>
        </div>
      )}
    </li>
  );
}
